import React, { useCallback, useEffect, useState } from 'react';
import { DatePicker, message, Spin } from 'antd';
import { DownOutlined, FileTextOutlined, InfoCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { auditLogService, AuditLogEntry } from './AuditLogService';
import styles from './AuditLog.module.css';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

// ── Stub entries shown when API returns 404 ────────────────────────────────────

const STUB_ENTRIES: AuditLogEntry[] = [
  {
    id: 'stub-1',
    timestamp: '2026-05-19T14:15:00',
    userName: 'James Thornton',
    userRole: 'Firm Admin',
    module: 'Clients',
    action: 'Updated',
    actionClass: 'updated',
    target: 'Acme Corporation Holdings — Primary contact',
    fields: [
      { field: 'Primary Contact',  previousValue: 'Daniel Okafor',    newValue: 'Margaret Cole' },
      { field: 'Contact Email',    previousValue: 'daniel@acme.com',  newValue: 'contact@acme.com' },
    ],
    isExpanded: false,
  },
  {
    id: 'stub-2',
    timestamp: '2026-05-18T10:30:00',
    userName: 'Marcus Chen',
    userRole: 'Senior Associate',
    module: 'Engagements',
    action: 'Scope changed',
    actionClass: 'updated',
    target: 'FY24 Annual Audit — Linked entities',
    fields: [
      { field: 'Linked Entities', previousValue: '3 entities', newValue: '5 entities' },
    ],
    isExpanded: false,
  },
  {
    id: 'stub-3',
    timestamp: '2026-05-17T09:00:00',
    userName: 'Sarah Jenkins',
    userRole: 'Manager',
    module: 'Entities',
    action: 'Created',
    actionClass: 'created',
    target: 'New Partnership: TechCorp EMEA',
    fields: [],
    isExpanded: false,
  },
];

// ── Module badge colors ────────────────────────────────────────────────────────

const MODULE_COLORS: Record<string, { bg: string; color: string }> = {
  'Clients':      { bg: '#dbeafe', color: '#1d4ed8' },
  'Engagements':  { bg: '#dcfce7', color: '#166534' },
  'Entities':     { bg: '#f3e8ff', color: '#7c3aed' },
  'Calculations': { bg: '#fef3c7', color: '#d97706' },
};

function getAvatarColor(name: string): string {
  const colors = ['#7c3aed', '#2563eb', '#0891b2', '#059669', '#d97706', '#dc2626', '#db2777'];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

function getInitials(name: string): string {
  return name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
}

function formatDate(ts: string): { day: string; time: string } {
  try {
    const d = new Date(ts);
    const day = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    return { day, time };
  } catch {
    return { day: ts, time: '' };
  }
}

function actionClass(cls: string): string {
  switch (cls) {
    case 'created':  return styles.actionCreated;
    case 'deleted':  return styles.actionDeleted;
    case 'updated':  return styles.actionUpdated;
    default:         return styles.actionScope;
  }
}

// ── Entry row ─────────────────────────────────────────────────────────────────

const EntryRow: React.FC<{
  entry: AuditLogEntry;
  expanded: boolean;
  onToggle: () => void;
}> = ({ entry, expanded, onToggle }) => {
  const { day, time } = formatDate(entry.timestamp);
  const modStyle = MODULE_COLORS[entry.module] ?? { bg: '#f3f4f6', color: '#6b7280' };

  return (
    <div className={styles.entry}>
      <div className={styles.entryHead} onClick={onToggle}>
        <div className={styles.entryDate}>
          <div className={styles.entryDateDay}>{day}</div>
          <div className={styles.entryDateTime}>{time}</div>
        </div>
        <div
          className={styles.avatar}
          style={{ background: getAvatarColor(entry.userName) }}
        >
          {getInitials(entry.userName)}
        </div>
        <div className={styles.entryUser}>
          <div className={styles.entryUserName}>{entry.userName}</div>
          <div className={styles.entryUserRole}>{entry.userRole}</div>
        </div>
        <span
          className={styles.moduleBadge}
          style={{ background: modStyle.bg, color: modStyle.color }}
        >
          {entry.module}
        </span>
        <span className={`${styles.actionText} ${actionClass(entry.actionClass)}`}>
          {entry.action}
        </span>
        <div className={styles.entryTarget}>{entry.target}</div>
        <DownOutlined className={expanded ? styles.chevronOpen : styles.chevron} />
      </div>

      {expanded && (
        <div className={styles.entryExpand}>
          {entry.fields.length > 0 ? (
            <>
              <div className={styles.expandDesc}>Change details</div>
              <table className={styles.diffTable}>
                <thead>
                  <tr>
                    <th style={{ width: 220 }}>Field</th>
                    <th>Previous Value</th>
                    <th style={{ width: 40 }}></th>
                    <th>New Value</th>
                  </tr>
                </thead>
                <tbody>
                  {entry.fields.map((f, i) => (
                    <tr key={i}>
                      <td className={styles.diffField}>{f.field}</td>
                      <td>
                        {f.previousValue != null
                          ? <span className={styles.diffPrev}>{f.previousValue}</span>
                          : <span style={{ color: 'var(--ce-text-muted)', fontStyle: 'italic' }}>—</span>}
                      </td>
                      <td className={styles.diffArrow}>→</td>
                      <td>
                        {f.newValue != null
                          ? <span className={styles.diffNew}>{f.newValue}</span>
                          : <span style={{ color: 'var(--ce-text-muted)', fontStyle: 'italic' }}>—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : (
            <div className={styles.expandDesc}>No field-level details recorded for this event.</div>
          )}
          <div className={styles.expandActions}>
            <button className={styles.btnOutline} onClick={() => message.info('Report discrepancy coming soon')}>
              Report discrepancy
            </button>
            <button className={styles.btnOutline} onClick={onToggle}>
              Hide details
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── AuditLog ──────────────────────────────────────────────────────────────────

export const AuditLog: React.FC = () => {
  const [entries, setEntries]       = useState<AuditLogEntry[]>([]);
  const [loading, setLoading]       = useState(false);
  const [isStub, setIsStub]         = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [moduleFilter, setModuleFilter] = useState('All modules');
  const [dateRange, setDateRange]   = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(30, 'day'),
    dayjs(),
  ]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await auditLogService.getLogs({
        module: moduleFilter !== 'All modules' ? moduleFilter : undefined,
        from:   dateRange[0].format('YYYY-MM-DD'),
        to:     dateRange[1].format('YYYY-MM-DD'),
      });
      setEntries(data);
      setIsStub(false);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404 || status === undefined) {
        setEntries(STUB_ENTRIES);
        setIsStub(true);
      } else {
        message.error('Failed to load audit log');
        setEntries([]);
        setIsStub(false);
      }
    } finally {
      setLoading(false);
    }
  }, [moduleFilter, dateRange]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const search = searchText.toLowerCase();
  const filtered = entries.filter(e =>
    e.userName.toLowerCase().includes(search) ||
    e.target.toLowerCase().includes(search) ||
    e.action.toLowerCase().includes(search),
  );

  return (
    <div className={styles.pageWrap}>
      <div className={styles.pageHead}>
        <h1 className={styles.pageTitle}>Audit Log</h1>
        <div className={styles.headActions}>
          <button className={styles.btnOutline} onClick={() => message.info('Show archives coming soon')}>
            Show Archives
          </button>
          <button className={styles.btnOutline} onClick={() => message.info('Archive logs coming soon')}>
            Archive logs
          </button>
        </div>
      </div>

      {isStub && (
        <div className={styles.sampleBanner}>
          <InfoCircleOutlined />
          Sample data — connect audit log API to show live entries
        </div>
      )}

      {/* Filter row */}
      <div className={styles.filterRow}>
        <RangePicker
          value={dateRange}
          onChange={(v) => { if (v && v[0] && v[1]) setDateRange([v[0], v[1]]); }}
          style={{ height: 36 }}
          allowClear={false}
        />
        <select
          className={styles.filterSelect}
          value={moduleFilter}
          onChange={e => setModuleFilter(e.target.value)}
        >
          <option>All modules</option>
          <option>Clients</option>
          <option>Engagements</option>
          <option>Entities</option>
          <option>Calculations</option>
        </select>
        <select className={styles.filterSelect} defaultValue="All users">
          <option>All users</option>
        </select>
        <div className={styles.searchWrap}>
          <SearchOutlined className={styles.searchIcon} />
          <input
            className={styles.searchInput}
            placeholder="Search entries..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
          />
        </div>
      </div>

      {/* Entries */}
      {loading ? (
        <div className={styles.loadingState}><Spin /></div>
      ) : filtered.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}><FileTextOutlined style={{ fontSize: 32, color: '#ADADAD' }} /></div>
          <div className={styles.emptyText}>
            {searchText ? 'No entries match your search' : 'No audit log entries found for this period'}
          </div>
        </div>
      ) : (
        <div className={styles.entriesList}>
          {filtered.map(entry => (
            <EntryRow
              key={entry.id}
              entry={entry}
              expanded={expandedId === entry.id}
              onToggle={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};
